/**
 * The years an estate figure is projected over, editable where the figure is shown.
 *
 * Owner, 25 September 2026: the village lets you change "after 5 years" on the walk-in tile and
 * watch the estate move; every module framed the same way should do the same. Nobody knows how long
 * care will be needed, so the useful question is how much the answer moves when the period changes —
 * and that is only asked if the control sits next to the number.
 *
 * One component so the control reads and behaves identically wherever it appears: the aged care and
 * village walk-in tiles, the Support at Home starting position, and the cashflow and Age Pension
 * position cards. The right-hand Financial Impact panel has carried its own "Project legacy over"
 * control since before this; this is the same idea, inline with the figure.
 */
import React from 'react';

export const MIN_PROJECTION_YEARS = 1;
export const MAX_PROJECTION_YEARS = 40;

export interface ProjectionYearsInputProps {
    years: number;
    onChange: (years: number) => void;
    /** Words before the box — "After" by default; pass "Estate in" and so on where the frame differs. */
    before?: React.ReactNode;
    /** Words after the box and the year/years noun — "staying put." on the village tile. */
    after?: React.ReactNode;
    /** Matches the surrounding type; the tile captions run small. */
    className?: string;
    ariaLabel?: string;
}

export function ProjectionYearsInput({
    years,
    onChange,
    before = 'After',
    after = null,
    className = '',
    ariaLabel = 'Years to project the estate over',
}: ProjectionYearsInputProps) {
    const value = Number(years) || 0;
    return (
        <span className={`inline-flex items-center gap-1 ${className}`}>
            {before}
            <input
                type="number"
                min={MIN_PROJECTION_YEARS}
                max={MAX_PROJECTION_YEARS}
                value={value}
                onChange={(e) => onChange(Math.max(MIN_PROJECTION_YEARS, Math.min(MAX_PROJECTION_YEARS, parseInt(e.target.value, 10) || MIN_PROJECTION_YEARS)))}
                aria-label={ariaLabel}
                className="w-14 rounded-md border border-slate-300 px-1.5 py-0.5 text-center text-[13px] font-semibold text-slate-900"
            />
            {value === 1 ? 'year' : 'years'}
            {after}
        </span>
    );
}

export default ProjectionYearsInput;
